/**
 * Operator-to-vehicle line, over the map.
 *
 * Speaks on the convoy (priority first), then lands in the crew Chat tab
 * so the same words are in the thread the phones already have open.
 */
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { sendVehicleAlert, type MissionRouteStatus } from '../../services/missionApi';
import { sendCrewMessage } from '../../services/crewChatApi';
import { formatVehicleCrewLine } from './crewChat';

const CHANNEL = 'general';
const GOLD = '#f5b942';
const BLUE = '#4ea1ff';
const CORAL = '#ef5350';

export interface MissionVehicleBarProps {
  missionId: number;
  enabled: boolean;
  onSent?: (status: MissionRouteStatus) => void;
}

const MissionVehicleBar: React.FC<MissionVehicleBarProps> = ({
  missionId,
  enabled,
  onSent,
}) => {
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    const message = text.trim();
    if (!message || busy || !enabled) return;
    setBusy(true);
    setError(null);
    try {
      const status = await sendVehicleAlert(
        missionId,
        message,
        urgent ? 'critical' : 'warning',
      );
      onSent?.(status);
      try {
        await sendCrewMessage(formatVehicleCrewLine(message, urgent), CHANNEL, 'alert');
      } catch {
        setError('Spoken on the vehicle — could not add it to Chat');
      }
      setText('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not reach the vehicle');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 16,
        right: 64,
        top: 10,
        // On the map only. Dialogs (setup, route planner) are 1300; sitting
        // above them covered the route editor.
        zIndex: 5,
        width: 'auto',
        maxWidth: 640,
        px: 1.25,
        py: 0.85,
        borderRadius: 2,
        bgcolor: 'rgba(12,16,22,0.55)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.58rem',
          fontWeight: 800,
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          color: 'rgba(232,235,238,0.55)',
          mb: 0.55,
        }}
      >
        Send to vehicle
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
        <Box
          component="input"
          value={text}
          disabled={!enabled || busy}
          placeholder={enabled ? 'Heard on the phone — keep it short' : 'Start the mission to reach the vehicle'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 30,
            px: 1,
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.12)',
            bgcolor: 'rgba(0,0,0,0.28)',
            color: '#f3f6f8',
            font: 'inherit',
            fontSize: '0.75rem',
            outline: 'none',
            '&::placeholder': { color: 'rgba(232,235,238,0.38)' },
            '&:focus': { borderColor: GOLD },
          }}
        />
        <Box
          component="button"
          type="button"
          disabled={!enabled}
          onClick={() => setUrgent((v) => !v)}
          sx={{
            height: 26,
            px: 1.1,
            borderRadius: 1,
            border: urgent ? '1px solid rgba(239,83,80,0.55)' : '1px solid rgba(255,255,255,0.14)',
            bgcolor: urgent ? 'rgba(239,83,80,0.28)' : 'rgba(255,255,255,0.06)',
            color: urgent ? '#ffb4b0' : 'rgba(232,235,238,0.72)',
            font: 'inherit',
            fontSize: '0.64rem',
            fontWeight: 700,
            cursor: enabled ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
          }}
        >
          {urgent ? 'Urgent' : 'Normal'}
        </Box>
        <Box
          component="button"
          type="button"
          disabled={!enabled || busy || !text.trim()}
          onClick={() => void send()}
          sx={{
            height: 26,
            px: 1.15,
            borderRadius: 1,
            border: `1px solid ${text.trim() && enabled ? 'rgba(78,161,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
            bgcolor: text.trim() && enabled ? 'rgba(78,161,255,0.2)' : 'transparent',
            color: text.trim() && enabled ? BLUE : '#5b6570',
            font: 'inherit',
            fontSize: '0.64rem',
            fontWeight: 700,
            cursor: !enabled || busy || !text.trim() ? 'default' : 'pointer',
          }}
        >
          {busy ? 'Sending' : 'Send'}
        </Box>
      </Box>
      {error && (
        <Typography sx={{ display: 'block', mt: 0.5, color: CORAL, fontSize: '0.62rem' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default MissionVehicleBar;
